import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, setCsrfToken, setUnauthorizedHandler } from '../api/client';
import type { SessionFeatures, SessionUser } from '../api/types';
import { Spinner } from '../components/ui';

export const SESSION_KEY = ['session'] as const;

/** Current user, or null when signed out. 401 is an answer here, not an error. */
export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        const res = await api.get<{
          user: SessionUser;
          csrfToken: string;
          features?: SessionFeatures;
        }>('/auth/session', { skipUnauthorizedRedirect: true });
        return res.features ? { ...res.user, features: res.features } : res.user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setCsrfToken(null);
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Re-reads the session after sign-in/out. Unlike invalidateQueries this also refetches when no
 * component is currently observing the session (e.g. on the 2FA page), so the next guard sees
 * the fresh user instead of the stale `null` cached on /login.
 */
export function refreshSession(queryClient: QueryClient) {
  return queryClient.refetchQueries({ queryKey: SESSION_KEY, type: 'all' });
}

// The login → 2FA hand-off lives in memory only; a reload restarts login (safe default).
let mfaPending = false;
export const mfaFlow = {
  start: () => void (mfaPending = true),
  finish: () => void (mfaPending = false),
  reset: () => void (mfaPending = false),
  isPending: () => mfaPending,
};

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner />
    </div>
  );
}

/** Root layout: wires the API client's 401 handler to the router and query cache. */
export function RootLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The router's location, not window.location: they only coincide in a browser router, and a
  // stale 401 must still send the user back to where they are now (flaky under memory routing).
  const location = useLocation();
  const here = useRef(location.pathname + location.search);
  useEffect(() => {
    here.current = location.pathname + location.search;
  }, [location]);
  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.setQueryData(SESSION_KEY, null);
      const next = here.current;
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, queryClient]);
  return <Outlet />;
}

export function RequireSession({ children }: { children?: ReactNode }) {
  const { data: user, isPending } = useSession();
  const location = useLocation();
  if (isPending) return <FullPageSpinner />;
  if (!user) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }
  return children ?? <Outlet />;
}

export function RedirectIfSignedIn({ children }: { children?: ReactNode }) {
  const { data: user, isPending } = useSession();
  const [params] = useSearchParams();
  if (isPending) return <FullPageSpinner />;
  if (user) return <Navigate to={safeNext(params.get('next'))} replace />;
  return children ?? <Outlet />;
}

export function RequireMfaPending({ children }: { children?: ReactNode }) {
  // Decided once on entry: re-renders while the 2FA step completes must not bounce to /login.
  const [allowed] = useState(mfaFlow.isPending);
  if (!allowed) return <Navigate to="/login" replace />;
  return children ?? <Outlet />;
}

/** Only allow same-app relative paths as post-login destinations (no open redirect). */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}
